<template>
  <div :class="{ 'skeleton-box': apiStatus === 'LOADING' && !objectHasEntries(selectedVariant) }">
    <div class="car-detail-overview white-card">
      <div class="d-flex items-center space-between">
        <h3 class="no-margin">Overview</h3>
        <CompareButton :vehicle-id="carData.Vehicle_UID" style="width: 8rem" variant="primary" />
      </div>

      <DropDown
        v-if="badgeList?.length"
        class="car-tile__badge-list"
        :selected-item="selectedVariant"
        @selected-changed="updateVariant"
        :list-data="badgeList"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import DropDown from '../form-fields/DropDown.vue';
import CompareButton from '../buttons/CompareButton.vue';
import { useUrlBuilder } from '../../composables/useUrlBuilder';
import { useCarAvailableStore } from '../../foundation/stores/carAvailableStore';
import { IDropDownItem } from '../../foundation/interfaces/IDropDownItem';
import { IVariant } from '../../foundation/interfaces/IVariant';
import { useAnalytics } from '../../composables/useAnalytics';
import { AnalyticsEventNames, AnalyticsEventTypes, AnalyticsEvents } from '../../foundation/enums/AnalyticsEvents';
import { objectHasEntries } from '../../foundation/helpers';
import { NO_VARIANT_TEXT } from '../../foundation/appConstants';
import { useCarData } from '../../composables/useCarData';
import { IVehicleGroup } from '../../foundation/interfaces/IVehicleGroup';

const props = defineProps({
  carData: {
    type: Object,
    required: true,
  },
  apiStatus: {
    type: String,
    default: 'LOADING',
  },
});

const emit = defineEmits<{
  (e: 'update:variantUpdate', vehicleId: string): void;
}>();

const carAvailableStore = useCarAvailableStore();
const vehicleGroup = ref<IVehicleGroup | null>();
const analytics = useAnalytics();
const urlBuilder = useUrlBuilder();
const selectedVariant = ref({} as IDropDownItem);
const { getVariantYearRange } = useCarData(props.carData);

onMounted(async () => {
  await getVehicleGroup(); // Get the list of variants for this vehicle
  pushToAnalytics();
});

async function getVehicleGroup() {
  vehicleGroup.value = (await carAvailableStore.getVehicleGroupByVehicleUid(props.carData.Vehicle_UID)) as IVehicleGroup;
  if (!vehicleGroup?.value) {
    return;
  }
  const currentVariant = vehicleGroup.value.variants.filter((x) => x.id === props.carData.Vehicle_UID)[0];
  selectedVariant.value = setVariantValues(currentVariant) as IDropDownItem;
}

function setVariantValues(currentVariant: IVariant): IDropDownItem {
  const labelText = currentVariant?.badgeDetails === '-' ? NO_VARIANT_TEXT : currentVariant.badgeDetails;
  return {
    label: labelText,
    value: currentVariant.id,
    suffix: '(' + getVariantYearRange(currentVariant) + ')',
    tooltip: labelText,
  } as IDropDownItem;
}

function updateVariant(selectedId: IDropDownItem) {
  window.history.pushState(
    null,
    '',
    urlBuilder.getCarDetailsUrl(selectedId.value, `${props.carData.Year_low}-${props.carData.Year_high}`, props.carData.Make, props.carData.Model)
  );
  emit('update:variantUpdate', selectedId.value);
}

const badgeList = computed(() => {
  let dropList = [] as IDropDownItem[];

  if (vehicleGroup?.value?.variants?.length) {
    dropList.push(selectedVariant.value);

    vehicleGroup.value.variants
      .filter((x) => x.id !== props.carData.Vehicle_UID)
      .forEach((currentVariant) => {
        dropList.push({
          ...setVariantValues(currentVariant),
        });
      });
  }

  return dropList;
});

function pushToAnalytics() {
  try {
    analytics.push(AnalyticsEvents.NavigationClickToView, {
      eventInfo: {
        navigation: {
          name: AnalyticsEventNames.View,
          type: AnalyticsEventTypes.CarResults,
          makeName: props.carData.Make,
          modelName: props.carData.Model,
          badgeName: props.carData.Trim,
          year: `${props.carData.Year_low} - ${props.carData.Year_high}`,
          bodyType: props.carData.Body_type,
          engineType: props.carData.Overall_FuelType,
          URL: urlBuilder.getCarDetailsUrl(
            props.carData.Vehicle_UID,
            `${props.carData.Year_low} - ${props.carData.Year_high}`,
            props.carData.Make?.toLowerCase(),
            props.carData.Model.toLowerCase()
          ),
        },
      },
    });
  } catch (error) {
    console.warn('fail to push data to analytics', error);
  }
}
</script>

<style lang="scss" scoped>
.car-detail-overview {
  padding: 1rem;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
</style>
